import type { RoomVariant } from "@/types/state";
import { GameOverReason } from "@/types/state";
import {Room} from "@/types/api";

type RoomRequirements = {
    minCashRequired?: number | null;
    minDepotRequired?: number | null;
} | null;

function pickByThreshold(
    roomA: RoomRequirements,
    roomB: RoomRequirements,
    currentValue: number,
    getMin: (room: NonNullable<RoomRequirements>) => number,
): RoomVariant {
    if (roomA === null) return "b";
    if (roomB === null) return "a";

    const minA = getMin(roomA);
    const minB = getMin(roomB);

    if (minA >= minB) {
        return currentValue >= minA ? "a" : "b";
    }
    return currentValue >= minB ? "b" : "a";
}

/**
 * Pick room variant A or B by current cash.
 * Chooses the room with the higher minCashRequired if current cash is at least that value;
 * otherwise chooses the other variant.
 */
export function pickRoomVariantByCash(
    roomA: RoomRequirements,
    roomB: RoomRequirements,
    currentCash: number,
): RoomVariant {
    return pickByThreshold(roomA, roomB, currentCash, (r) => r.minCashRequired ?? 0);
}

/**
 * Pick room variant A or B by current depot (investments).
 * Same logic as cash but uses minDepotRequired and currentDepot.
 */
export function pickRoomVariantByDepot(
    roomA: RoomRequirements,
    roomB: RoomRequirements,
    currentDepot: number,
): RoomVariant {
    return pickByThreshold(roomA, roomB, currentDepot, (r) => r.minDepotRequired ?? 0);
}

/**
 * Pick room variant for the next phase. Uses depot when any room has minDepotRequired > 0,
 * otherwise uses cash.
 */
export function pickRoomVariant(
    roomA: RoomRequirements,
    roomB: RoomRequirements,
    currentCash: number,
    currentDepot: number,
): RoomVariant {
    const useDepot = (roomA?.minDepotRequired ?? 0) > 0 || (roomB?.minDepotRequired ?? 0) > 0;

    if (useDepot) {
        return pickRoomVariantByDepot(roomA, roomB, currentDepot);
    }
    return pickRoomVariantByCash(roomA, roomB, currentCash);
}

export function getEndScreenRoomImage({ room, gameOverReason }: { room: Room; gameOverReason: GameOverReason }) {
    if (gameOverReason !== "none") {
        // Sad image is optional while broke image is guaranteed, so check unhappy condition first
        if (gameOverReason === "cash" && room.gameOverSadVersion) {
            return room.gameOverSadVersion;
        }

        if (room.gameOverBrokeVersion) {
            return room.gameOverBrokeVersion;
        }
    }

    return room.image;
}
